import React from 'react';
import { Select, MenuItem, InputLabel, FormControl, TextField, Grid, Typography, SelectChangeEvent, RadioGroup, FormControlLabel, Radio, FormLabel } from '@mui/material';
import { Alert } from '@mui/lab';
import { Translate } from 'react-localize-redux';

interface DispositionValue {
    disposition: string;
    dateOfDeath?: string;
    timeOfDeath?: string;
    placeOfDeath?: string;
    deathConfirmedBy?: string;
    immediateCause?: string;
    underlyingCause?: string;
    palliativePathway?: boolean;
    cause?: string; // legacy field, maps to immediateCause
}

interface DispositionFieldProps {
    input: any;
    meta: any;
    label: string;
    disabled?: boolean;
    translate: (key: string) => string;
}

const DISPOSITION_OPTIONS = [
    { value: "HOME", labelKey: "patient.disposition.home" },
    { value: "TRANSFER", labelKey: "patient.disposition.transfer" },
    { value: "DECEASED", labelKey: "patient.disposition.deceased" },
    { value: "LEFT_AMA", labelKey: "patient.disposition.left-ama" },
    { value: "OTHER", labelKey: "patient.disposition.other" },
];

const PLACE_OF_DEATH_OPTIONS = [
    { value: "HOME", labelKey: "patient.death.place-home" },
    { value: "HOSPICE", labelKey: "patient.death.place-hospice" },
    { value: "HOSPITAL", labelKey: "patient.death.place-hospital" },
    { value: "OTHER", labelKey: "patient.death.place-other" },
    { value: "UNKNOWN", labelKey: "patient.death.place-unknown" },
];

const CONFIRMED_BY_OPTIONS = [
    { value: "PHYSICIAN", labelKey: "patient.death.confirmed-by-physician" },
    { value: "NURSE", labelKey: "patient.death.confirmed-by-nurse" },
    { value: "FAMILY", labelKey: "patient.death.confirmed-by-family" },
];

/**
 * Maps legacy disposition values (from old select field) to new enum values.
 * Old submissions stored free-text like "Home care", "Acute care", etc.
 */
const LEGACY_VALUE_MAP: Record<string, string> = {
    "Home care": "HOME",
    "home care": "HOME",
    "Acute care": "TRANSFER",
    "acute care": "TRANSFER",
    "Transferred to another facility": "TRANSFER",
    "transferred to another facility": "TRANSFER",
    "No further medical care": "OTHER",
    "no further medical care": "OTHER",
    "Outpatient care": "HOME",
    "outpatient care": "HOME",
    "Other": "OTHER",
    "other": "OTHER",
};

function normalizeLegacyValue(raw: string): string {
    return LEGACY_VALUE_MAP[raw] || raw;
}

const DispositionField: React.FC<DispositionFieldProps> = ({
    input,
    label,
    meta,
    disabled,
    translate
}) => {
    const error = meta.touched && meta.error;
    const labelId = `${input.name}_label`;

    // Parse current value — can be a string or DispositionValue object
    // Also handles legacy values from old select field (e.g. "Home care" → "HOME")
    let currentValue: DispositionValue;
    if (typeof input.value === 'object' && input.value?.disposition) {
        // Backward compat: old records have `cause` instead of `immediateCause`
        currentValue = {
            ...input.value,
            immediateCause: input.value.immediateCause || input.value.cause || "",
        };
    } else {
        const rawStr = typeof input.value === 'string' ? input.value : "";
        currentValue = { disposition: normalizeLegacyValue(rawStr), dateOfDeath: "", immediateCause: "" };
    }

    function handleDispositionChange(event: SelectChangeEvent<string>) {
        const newDisposition = event.target.value;
        if (newDisposition === "DECEASED") {
            input.onChange({
                disposition: newDisposition,
                dateOfDeath: currentValue.dateOfDeath || "",
                timeOfDeath: currentValue.timeOfDeath || "",
                placeOfDeath: currentValue.placeOfDeath || "",
                deathConfirmedBy: currentValue.deathConfirmedBy || "",
                immediateCause: currentValue.immediateCause || "",
                underlyingCause: currentValue.underlyingCause || "",
                palliativePathway: currentValue.palliativePathway ?? null,
            });
        } else {
            input.onChange({ disposition: newDisposition });
        }
    }

    function handleFieldChange(field: string, value: any) {
        input.onChange({ ...currentValue, [field]: value });
    }

    const isDeceased = currentValue.disposition === "DECEASED";

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <FormControl fullWidth variant="outlined" margin="dense" error={!!error}>
                    <InputLabel id={labelId}>{label}</InputLabel>
                    <Select
                        labelId={labelId}
                        id={input.name}
                        label={label}
                        disabled={disabled}
                        value={currentValue.disposition}
                        onChange={handleDispositionChange}
                        data-testid="disposition-field-select"
                    >
                        {DISPOSITION_OPTIONS.map((option) => (
                            <MenuItem value={option.value} key={option.value}>
                                {translate(option.labelKey)}
                            </MenuItem>
                        ))}
                    </Select>
                    {error && (
                        <span style={{ color: '#d32f2f', fontSize: 12 }}>{meta.error}</span>
                    )}
                </FormControl>
            </Grid>
            {isDeceased && (
                <>
                    <Grid item xs={12}>
                        <Alert severity="warning">
                            <Translate id="patient.status.confirm-deceased" />
                        </Alert>
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            type="datetime-local"
                            variant="outlined"
                            size="small"
                            label={translate("patient.death.date")}
                            value={currentValue.dateOfDeath || ""}
                            onChange={(e) => handleFieldChange("dateOfDeath", e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            required
                            data-testid="disposition-death-date"
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            type="time"
                            variant="outlined"
                            size="small"
                            label={translate("patient.death.time")}
                            value={currentValue.timeOfDeath || ""}
                            onChange={(e) => handleFieldChange("timeOfDeath", e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            data-testid="disposition-death-time"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl component="fieldset">
                            <FormLabel component="legend">
                                {translate("patient.death.place")}
                            </FormLabel>
                            <RadioGroup
                                row
                                value={currentValue.placeOfDeath || ""}
                                onChange={(e) => handleFieldChange("placeOfDeath", e.target.value)}
                                data-testid="disposition-death-place"
                            >
                                {PLACE_OF_DEATH_OPTIONS.map((opt) => (
                                    <FormControlLabel
                                        key={opt.value}
                                        value={opt.value}
                                        control={<Radio size="small" />}
                                        label={translate(opt.labelKey)}
                                    />
                                ))}
                            </RadioGroup>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl component="fieldset">
                            <FormLabel component="legend">
                                {translate("patient.death.confirmed-by")}
                            </FormLabel>
                            <RadioGroup
                                row
                                value={currentValue.deathConfirmedBy || ""}
                                onChange={(e) => handleFieldChange("deathConfirmedBy", e.target.value)}
                                data-testid="disposition-death-confirmed-by"
                            >
                                {CONFIRMED_BY_OPTIONS.map((opt) => (
                                    <FormControlLabel
                                        key={opt.value}
                                        value={opt.value}
                                        control={<Radio size="small" />}
                                        label={translate(opt.labelKey)}
                                    />
                                ))}
                            </RadioGroup>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            size="small"
                            label={translate("patient.death.immediate-cause")}
                            value={currentValue.immediateCause || ""}
                            onChange={(e) => handleFieldChange("immediateCause", e.target.value)}
                            required
                            data-testid="disposition-death-immediate-cause"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            multiline
                            rows={2}
                            variant="outlined"
                            size="small"
                            label={translate("patient.death.underlying-cause")}
                            value={currentValue.underlyingCause || ""}
                            onChange={(e) => handleFieldChange("underlyingCause", e.target.value)}
                            data-testid="disposition-death-underlying-cause"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl component="fieldset">
                            <FormLabel component="legend">
                                {translate("patient.death.palliative-pathway")}
                            </FormLabel>
                            <RadioGroup
                                row
                                value={currentValue.palliativePathway === true ? "yes" : currentValue.palliativePathway === false ? "no" : ""}
                                onChange={(e) => handleFieldChange("palliativePathway", e.target.value === "yes")}
                                data-testid="disposition-death-palliative"
                            >
                                <FormControlLabel value="yes" control={<Radio size="small" />} label={translate("general.yes")} />
                                <FormControlLabel value="no" control={<Radio size="small" />} label={translate("general.no")} />
                            </RadioGroup>
                        </FormControl>
                    </Grid>
                </>
            )}
        </Grid>
    );
};

export default DispositionField;
